"use client";

import React, { useState, useCallback, useMemo } from "react";
import { ResponsiveModal, MODAL_IDS, useGlobalModalStack } from "@wtc/ui";
import { Button } from "@/components/ui/button";
import { Plus, User } from "lucide-react";
import { createEnhancedGenericForm } from "@/components/shared/EnhancedGenericCreateForm";
import { ENHANCED_CONTACTS_CONFIG } from "@/features/contacts/components/EnhancedContactsConfig";
import { useCreateClient } from "@/features/contacts/hooks";
import { Client, CreateContactForm as CreateContactFormType } from "@/lib/types/database";
import type { Database } from "@/lib/supabase/database.types";

/** The row `useCreateClient` returns after a successful insert. */
type CreatedContactRow = Database["public"]["Tables"]["contacts"]["Row"];

/** Minimal shape of a trigger element we can chain an onClick onto. */
type ClickableTriggerProps = { onClick?: React.MouseEventHandler<HTMLElement> };

interface QuickContactCreationModalProps {
  onContactCreated?: (contactId: string, contactData?: CreatedContactRow) => void;
  trigger?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  // Optional: Pre-fill form with existing contact data (for company-based creation)
  existingContact?: Client;
  // Optional: Custom modal title and description
  title?: string;
  description?: string;
}

export function QuickContactCreationModal({
  onContactCreated,
  trigger,
  open,
  onOpenChange,
  existingContact,
  title,
  description,
}: QuickContactCreationModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const createClientMutation = useCreateClient();
  const { openModal, closeModal, isModalOpen } = useGlobalModalStack();

  // Use global modal stack if no controlled state is provided
  const useGlobalStack = open === undefined && onOpenChange === undefined;
  const globalModalId = MODAL_IDS.contacts.createQuick;

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (useGlobalStack) {
        if (newOpen) {
          openModal(globalModalId, { companyName: existingContact?.company_name });
        } else {
          closeModal(globalModalId);
        }
      } else if (onOpenChange) {
        onOpenChange(newOpen);
      } else {
        setIsOpen(newOpen);
      }
    },
    [onOpenChange, useGlobalStack, openModal, closeModal, globalModalId, existingContact?.company_name]
  );

  // When using global stack, read from global stack. Otherwise use controlled or local state.
  const currentOpen = useGlobalStack 
    ? isModalOpen(globalModalId)
    : (open !== undefined ? open : isOpen);

  // Pre-fill form data with existing contact's company information (if provided)
  const initialValues = useMemo((): Partial<CreateContactFormType> => {
    if (!existingContact) return {};

    return {
      company_name: existingContact.company_name || "",
      status: existingContact.status || "lead",
      source: existingContact.source || "Company colleague",
      industry: existingContact.companies?.industry ?? "",
      // Copy address information if available
      city: existingContact.city || "",
      state: existingContact.state || "",
      country: existingContact.country || "",
      // Default to active
      is_active: true,
    };
  }, [existingContact]);

  const handleSubmit = useCallback(
    async (formData: CreateContactFormType) => {
      try {
        // Merge form data with pre-filled company information if existingContact is provided
        const contactData: CreateContactFormType = existingContact
          ? {
              ...formData,
              // Ensure company info is preserved if not explicitly changed
              company_name: formData.company_name || existingContact.company_name || "",
              status: formData.status || existingContact.status || "lead",
              source: formData.source || existingContact.source || "Company colleague",
              industry: (formData.industry || existingContact?.companies?.industry) ?? "",
              is_active: formData.is_active !== undefined ? formData.is_active : true,
            }
          : formData;

        const result = await createClientMutation.mutateAsync(contactData);

        // Call the parent callback if provided
        if (onContactCreated) {
          onContactCreated(result.id, result);
        }

        // Close modal after successful creation
        handleOpenChange(false);
      } catch (error) {
        // Error handling is done by the mutation hook
        throw error;
      }
    },
    [createClientMutation, onContactCreated, handleOpenChange, existingContact]
  );

  // If trigger is provided, enhance it by attaching our onClick while preserving existing onClick
  if (trigger) {
    const enhancedTrigger = React.isValidElement<ClickableTriggerProps>(trigger) ? (
      React.cloneElement(trigger, {
        onClick: (e: React.MouseEvent<HTMLElement>) => {
          // Call original onClick if present
          trigger.props.onClick?.(e);
          // Then open the modal
          handleOpenChange(true);
        },
      })
    ) : (
      <div onClick={() => handleOpenChange(true)}>{trigger}</div>
    );

    return (
      <>
        {enhancedTrigger}
        {useGlobalStack ? (
          <ResponsiveModal
            globalModalId={globalModalId}
            title={
              title ||
              (existingContact
                ? `Add Contact to ${existingContact.company_name || "Company"}`
                : "Quick Create Contact")
            }
            description={
              description ||
              (existingContact
                ? `Add a new contact to the same company as ${existingContact.name}`
                : undefined)
            }
            maxWidth="2xl"
            side="bottom"
            showCloseButton={true}
          >
            {React.createElement(
              createEnhancedGenericForm("contacts", ENHANCED_CONTACTS_CONFIG),
              {
                onSubmit: (data: Partial<Client>) =>
                  handleSubmit(data as unknown as CreateContactFormType),
                initialValues: initialValues,
              }
            )}
          </ResponsiveModal>
        ) : (
          <ResponsiveModal
            open={currentOpen}
            onOpenChange={handleOpenChange}
            title={
              title ||
              (existingContact
                ? `Add Contact to ${existingContact.company_name || "Company"}`
                : "Quick Create Contact")
            }
            description={
              description ||
              (existingContact
                ? `Add a new contact to the same company as ${existingContact.name}`
                : undefined)
            }
            maxWidth="2xl"
            showCloseButton={true}
          >
            {React.createElement(
              createEnhancedGenericForm("contacts", ENHANCED_CONTACTS_CONFIG),
              {
                onSubmit: (data: Partial<Client>) =>
                  handleSubmit(data as unknown as CreateContactFormType),
                initialValues: initialValues,
              }
            )}
          </ResponsiveModal>
        )}
      </>
    );
  }

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="flex items-center gap-2"
        onClick={() => handleOpenChange(true)}
      >
        <Plus className="h-4 w-4" />
        Quick Create Contact
      </Button>

      {useGlobalStack ? (
        <ResponsiveModal
          globalModalId={globalModalId}
          title={
            title ||
            (existingContact
              ? `Add Contact to ${existingContact.company_name || "Company"}`
              : "Quick Create Contact")
          }
          description={
            description ||
            (existingContact
              ? `Add a new contact to the same company as ${existingContact.name}`
              : undefined)
          }
          maxWidth="2xl"
          side="bottom"
          showCloseButton={true}
        >
          {React.createElement(
            createEnhancedGenericForm("contacts", ENHANCED_CONTACTS_CONFIG),
            {
              onSubmit: (data: Partial<Client>) =>
                handleSubmit(data as unknown as CreateContactFormType),
              initialValues: initialValues,
            }
          )}
        </ResponsiveModal>
      ) : (
        <ResponsiveModal
          open={currentOpen}
          onOpenChange={handleOpenChange}
          title={
            title ||
            (existingContact
              ? `Add Contact to ${existingContact.company_name || "Company"}`
              : "Quick Create Contact")
          }
          description={
            description ||
            (existingContact
              ? `Add a new contact to the same company as ${existingContact.name}`
              : undefined)
          }
          maxWidth="2xl"
          showCloseButton={true}
        >
          {React.createElement(
            createEnhancedGenericForm("contacts", ENHANCED_CONTACTS_CONFIG),
            {
              onSubmit: (data: Partial<Client>) =>
                handleSubmit(data as unknown as CreateContactFormType),
              initialValues: initialValues,
            }
          )}
        </ResponsiveModal>
      )}
    </>
  );
}
